import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Globe, ChevronDown, ChevronUp } from 'lucide-react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { useStateInternationalStats, type InternationalScenarioCost } from '@/hooks/useStateInternationalStats';
import { getCountrySlug } from '@/lib/countryUtils';
import { Flag } from '@/components/Flag';

interface ScenarioProcedure {
  code: string;
  codeType: string;
  description: string;
  phase: string;
  quantity: number;
  estimatedCost: number;
}

interface ScenarioInternationalComparisonProps {
  scenarioId: string;
  scenarioName: string;
  scenarioSlug?: string;
  usNationalTotal: number;
  procedures: ScenarioProcedure[];
}

export function ScenarioInternationalComparison({
  scenarioId,
  scenarioName,
  scenarioSlug,
  usNationalTotal,
  procedures,
}: ScenarioInternationalComparisonProps) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  const {
    scenarioCosts,
    countries,
    procedurePrices,
    procedureNames,
    isLoading: statsLoading,
  } = useStateInternationalStats();

  // Filter scenario costs for this scenario
  const costsForScenario = useMemo(() => {
    if (!scenarioCosts?.length) return [];
    return scenarioCosts
      .filter((c: InternationalScenarioCost) => c.scenario_id === scenarioId)
      .sort((a: InternationalScenarioCost, b: InternationalScenarioCost) =>
        Number(b.intl_total_cost) - Number(a.intl_total_cost)
      );
  }, [scenarioCosts, scenarioId]);

  // Get CPT codes from procedures
  const cptCodes = useMemo(() => {
    return [...new Set(
      procedures
        .filter(p => p.codeType?.toUpperCase() === 'CPT')
        .map(p => p.code)
    )];
  }, [procedures]);

  // Coverage info from the first row (all rows have the same total)
  const mappedCount = costsForScenario[0]?.mapped_cpt_codes ?? 0;
  const totalCount = costsForScenario[0]?.total_cpt_codes ?? cptCodes.length;

  // Build name lookup from static data
  const nameMap = useMemo(() => {
    const m = new Map<string, string>();
    if (procedureNames?.length) {
      for (const n of procedureNames) {
        m.set(n.code, n.llm_quick_name || n.name);
      }
    }
    // Fallback to scenario procedure descriptions
    for (const p of procedures) {
      if (!m.has(p.code)) m.set(p.code, p.description);
    }
    return m;
  }, [procedureNames, procedures]);

  // Build pivot table from static data: { code -> { country -> price } }
  const pivotData = useMemo(() => {
    if (!procedurePrices?.length) return new Map<string, Map<string, number>>();
    const cptSet = new Set(cptCodes);
    const pivot = new Map<string, Map<string, number>>();
    for (const row of procedurePrices) {
      if (!cptSet.has(row.us_code)) continue;
      if (!pivot.has(row.us_code)) pivot.set(row.us_code, new Map());
      pivot.get(row.us_code)!.set(row.country_code, row.price_usd);
    }
    return pivot;
  }, [procedurePrices, cptCodes]);

  // Top countries to show in detail table (sorted by total cost desc, max 6)
  const detailCountries = useMemo(() => {
    return costsForScenario.slice(0, 6).map(c => c.country_code);
  }, [costsForScenario]);

  if (statsLoading) return null;

  if (costsForScenario.length === 0) {
    return (
      <Card
        className={isAhp ? '' : 'border-blue-400/20 bg-black/40 backdrop-blur-sm'}
        style={isAhp ? { backgroundColor: ahpDark ? 'var(--civic-card-dark)' : '#fff', border: ahpDark ? '1px solid rgba(var(--civic-gold-rgb),0.2)' : '1px solid rgba(var(--civic-ink-rgb),0.12)', boxShadow: ahpDark ? '0 2px 16px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,0.06)' } : undefined}
      >
        <CardHeader className="pb-2">
          <CardTitle
            className={isAhp ? "text-lg flex items-center gap-2" : "text-lg font-mono text-blue-400 flex items-center gap-2"}
            style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", color: ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy)', fontWeight: 400 } : undefined}
          >
            <Globe className="w-5 h-5" /> International Price Comparison
          </CardTitle>
          <CardDescription className="text-xs">
            How this scenario's cost compares to other countries
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-sm text-muted-foreground">
            <Globe className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p>International price mapping for this scenario is being compiled.</p>
            <p className="text-xs mt-1 opacity-60">CPT code crosswalk data is available for {cptCodes.length} procedures in this scenario.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const usTotal = Number(usNationalTotal) || Number(costsForScenario[0]?.us_national_total_cost) || 0;
  if (usTotal <= 0) return null;

  const maxCost = usTotal;

  const formatCurrency = (val: number) => {
    if (val >= 10000) return `$${(val / 1000).toFixed(1)}k`;
    if (val >= 1000) return `$${val.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    if (val >= 1) return `$${val.toFixed(0)}`;
    return `$${val.toFixed(2)}`;
  };

  const formatMultiplier = (usCost: number, intlCost: number) => {
    if (intlCost <= 0) return '';
    const mult = usCost / intlCost;
    if (mult >= 100) return `${mult.toFixed(0)}× cheaper`;
    if (mult >= 10) return `${mult.toFixed(1)}× cheaper`;
    return `${mult.toFixed(1)}× cheaper`;
  };

  const cardStyle = isAhp
    ? { backgroundColor: ahpDark ? 'var(--civic-card-dark)' : '#fff', border: ahpDark ? '1px solid rgba(var(--civic-gold-rgb),0.2)' : '1px solid rgba(var(--civic-ink-rgb),0.12)', boxShadow: ahpDark ? '0 2px 16px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,0.06)' }
    : undefined;

  const hasProcedureData = pivotData.size > 0;

  return (
    <Card
      className={isAhp ? '' : 'border-matrix-green/20 bg-black/40 backdrop-blur-sm'}
      style={cardStyle}
    >
      <CardHeader>
        <CardTitle
          className={isAhp ? "text-lg flex items-center gap-2" : "text-lg font-mono text-matrix-green flex items-center gap-2"}
          style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", color: ahpDark ? 'var(--civic-navy-soft)' : 'var(--civic-navy)', fontWeight: 400 } : undefined}
        >
          <Globe className="w-5 h-5" style={isAhp ? { color: 'var(--civic-gold)' } : undefined} />
          International Comparison
        </CardTitle>
        <CardDescription style={isAhp ? { color: ahpDark ? 'var(--civic-muted)' : '#666' } : undefined}>
          This scenario costs{' '}
          <strong style={{ color: ahpDark ? '#e8c469' : '#b8860b' }}>
            {formatCurrency(usTotal)}
          </strong>{' '}
          in the USA — here's what it costs elsewhere
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Summary bars */}
        <div className="space-y-2">
          {costsForScenario.map((row) => {
            const country = countries.get(row.country_code);
            const intlCost = Number(row.intl_total_cost);
            // Same-subset baseline: intl_total_cost only sums the CPT codes mapped
            // for THIS country, and us_national_total_cost is the US price of that
            // identical subset. Comparing against the full scenario total (usTotal)
            // manufactured absurd "442× cheaper" multipliers for partial coverage.
            const rowUsBaseline = Number(row.us_national_total_cost) > 0
              ? Number(row.us_national_total_cost)
              : usTotal;
            const barWidth = rowUsBaseline > 0
              ? Math.min(100, Math.max((intlCost / rowUsBaseline) * 100, 2))
              : 2;
            const multiplierText = formatMultiplier(rowUsBaseline, intlCost);
            const rowMapped = Number(row.mapped_cpt_codes) || 0;
            const slug = getCountrySlug(row.country_code);
            const linkable = !!slug;

            const rowContent = (
              <>
                <div className="w-[140px] flex-shrink-0 flex items-center gap-1.5 truncate">
                  <Flag country={row.country_code} title={country?.country_name || row.country_code} className="text-base" />
                  <span
                    className="truncate text-xs"
                    style={{ color: ahpDark ? '#c0cdd9' : isAhp ? '#333' : undefined }}
                  >
                    {country?.country_name || row.country_code}
                  </span>
                </div>

                <div className="flex-1 h-6 rounded-sm overflow-hidden relative" style={{
                  backgroundColor: ahpDark ? 'rgba(255,255,255,0.06)' : isAhp ? '#f0f0f0' : 'rgba(0,255,0,0.05)',
                }}>
                  <div
                    className="h-full rounded-sm transition-all duration-500"
                    style={{
                      width: `${barWidth}%`,
                      backgroundColor: ahpDark ? 'rgba(var(--civic-money-soft-rgb),0.5)' : isAhp ? 'var(--civic-money)' : '#22c55e',
                    }}
                  />
                  <span className="absolute inset-y-0 left-2 flex items-center text-xs font-medium" style={{
                    color: ahpDark ? '#e0e8ef' : isAhp ? 'var(--civic-ink)' : '#fff',
                  }}>
                    {formatCurrency(intlCost)}
                  </span>
                </div>

                <div className="w-[100px] flex-shrink-0 text-right text-xs" style={{
                  color: ahpDark ? 'var(--civic-money-soft)' : isAhp ? 'var(--civic-money)' : '#22c55e',
                  fontWeight: 600,
                }}>
                  {multiplierText}
                  {rowMapped > 0 && (
                    <div
                      className="text-[9px] leading-tight"
                      style={{ color: ahpDark ? 'var(--civic-muted-2)' : isAhp ? '#888' : '#666', fontWeight: 400 }}
                      title={`Comparison uses the US price of the same ${rowMapped} mapped procedures — not the full scenario total.`}
                    >
                      based on the {rowMapped} procedures mapped in this country
                    </div>
                  )}
                </div>
              </>
            );

            return linkable ? (
              <Link
                key={row.country_code}
                to={`/international/${slug}`}
                className="flex items-center gap-2 text-sm hover:opacity-90 transition-opacity rounded-sm focus:outline-none focus:ring-2 focus:ring-offset-1"
                style={{
                  // @ts-ignore — tailwind ring color override
                  ['--tw-ring-color' as string]: ahpDark ? 'var(--civic-navy-soft)' : isAhp ? 'var(--civic-navy)' : '#22c55e',
                }}
                title={t('scenarios:international.seeAllPrices', { country: country?.country_name || row.country_code })}
              >
                {rowContent}
              </Link>
            ) : (
              <div key={row.country_code} className="flex items-center gap-2 text-sm">
                {rowContent}
              </div>
            );
          })}
        </div>

        {/* Coverage + expand toggle */}
        <div className="flex items-center justify-between pt-1 border-t" style={{
          borderColor: ahpDark ? 'rgba(255,255,255,0.08)' : isAhp ? 'rgba(0,0,0,0.08)' : 'rgba(0,255,0,0.1)',
        }}>
          <span className="text-xs" style={{ color: ahpDark ? 'var(--civic-muted-2)' : isAhp ? '#888' : '#666' }}>
            {mappedCount} of {totalCount} CPT codes mapped internationally
          </span>
          {hasProcedureData && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-xs font-medium hover:underline"
              style={{ color: ahpDark ? 'var(--civic-navy-soft)' : isAhp ? 'var(--civic-navy)' : '#22c55e' }}
            >
              {expanded ? 'Hide' : 'Show'} procedure breakdown
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>

        {/* Expanded per-procedure table */}
        {expanded && hasProcedureData && (
          <div className="overflow-x-auto -mx-2 px-2 pt-2">
            <table className="w-full text-xs border-collapse" style={{ minWidth: '500px' }}>
              <thead>
                <tr style={{
                  borderBottom: `1px solid ${ahpDark ? 'rgba(255,255,255,0.1)' : isAhp ? 'rgba(0,0,0,0.1)' : 'rgba(0,255,0,0.2)'}`,
                }}>
                  <th className="text-left py-1.5 pr-2 font-medium" style={{
                    color: ahpDark ? 'var(--civic-muted)' : isAhp ? '#666' : '#888',
                  }}>Code</th>
                  <th className="text-left py-1.5 pr-2 font-medium" style={{
                    color: ahpDark ? 'var(--civic-muted)' : isAhp ? '#666' : '#888',
                  }}>Description</th>
                  <th className="text-right py-1.5 pr-2 font-medium" style={{
                    color: ahpDark ? '#e8c469' : isAhp ? '#b8860b' : '#fbbf24',
                  }}>USA</th>
                  {detailCountries.map(cc => {
                    const c = countries.get(cc);
                    const slug = getCountrySlug(cc);
                    return (
                      <th key={cc} className="text-right py-1.5 pr-2 font-medium" style={{
                        color: ahpDark ? 'var(--civic-muted)' : isAhp ? '#666' : '#888',
                      }}>
                        {slug ? (
                          <Link to={`/international/${slug}`} className="hover:underline hover:opacity-80 transition-opacity inline-flex items-center gap-1 justify-end">
                            <Flag country={cc} title={c?.country_name || cc} /> {cc}
                          </Link>
                        ) : (
                          <span className="inline-flex items-center gap-1 justify-end">
                            <Flag country={cc} title={c?.country_name || cc} /> {cc}
                          </span>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {cptCodes.map(code => {
                  const proc = procedures.find(p => p.code === code);
                  const usPrice = proc?.estimatedCost ?? 0;
                  const countryPrices = pivotData.get(code);

                  return (
                    <tr key={code} style={{
                      borderBottom: `1px solid ${ahpDark ? 'rgba(255,255,255,0.04)' : isAhp ? 'rgba(0,0,0,0.04)' : 'rgba(0,255,0,0.05)'}`,
                    }}>
                      <td className="py-1.5 pr-2 font-mono" style={{
                        color: ahpDark ? 'var(--civic-navy-soft)' : isAhp ? 'var(--civic-navy)' : '#22c55e',
                      }}>
                        <Link to={`/cpt/${code}`} className="hover:underline hover:opacity-80 transition-opacity">
                          {code}
                        </Link>
                      </td>
                      <td className="py-1.5 pr-2 max-w-[180px] truncate" style={{
                        color: ahpDark ? '#c0cdd9' : isAhp ? '#333' : '#ccc',
                      }}>{nameMap.get(code) || proc?.description || code}</td>
                      <td className="py-1.5 pr-2 text-right font-medium" style={{
                        color: ahpDark ? '#e8c469' : isAhp ? '#b8860b' : '#fbbf24',
                      }}>
                        {usPrice > 0 ? formatCurrency(usPrice) : '—'}
                      </td>
                      {detailCountries.map(cc => {
                        const price = countryPrices?.get(cc);
                        const cellColor = price != null && usPrice > 0
                          ? price < usPrice * 0.3
                            ? (ahpDark ? 'var(--civic-money-soft)' : isAhp ? '#16a34a' : '#22c55e')
                            : price < usPrice * 0.8
                            ? (ahpDark ? '#c0cdd9' : isAhp ? '#333' : '#ccc')
                            : (ahpDark ? '#f87171' : isAhp ? '#dc2626' : '#ef4444')
                          : (ahpDark ? '#445566' : isAhp ? '#bbb' : '#555');

                        const countrySlug = getCountrySlug(cc);
                        return (
                          <td key={cc} className="py-1.5 pr-2 text-right" style={{ color: cellColor }}>
                            {price != null && countrySlug ? (
                              <Link to={`/international/${countrySlug}#cpt_${code}`} className="hover:underline hover:opacity-80 transition-opacity">
                                {formatCurrency(price)}
                              </Link>
                            ) : (
                              price != null ? formatCurrency(price) : '—'
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Bottom CTA — full scenario calculator with this scenario pre-loaded */}
        <div className="pt-3 mt-2 border-t flex justify-end" style={{
          borderColor: ahpDark ? 'rgba(255,255,255,0.08)' : isAhp ? 'rgba(0,0,0,0.08)' : 'rgba(0,255,0,0.1)',
        }}>
          <Link
            to={scenarioSlug
              ? `/international/calculator/${scenarioId}/${scenarioSlug}`
              : '/international/calculator'}
            className="inline-flex items-center gap-1 text-xs font-semibold hover:underline transition-colors"
            style={{ color: ahpDark ? 'var(--civic-navy-soft)' : isAhp ? 'var(--civic-navy)' : '#22c55e' }}
          >
            {t('scenarios:international.compareAllPrices')}
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
